import { useEffect, useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DropdownFilter, FloatInput, FloatSelect, showToast } from "src/componentes";
import { ConsultaMunicipioModule } from "src/hook/Consulta/ConsultaMunicipio.Module";
import { MunicipioDto } from "src/pages/Privada/Entidade/Municipio/Municipio.Dto";
import {
  ETipoEventoMDFe,
} from "../../MDFe.Dto";
import {
  EventoCancelamentoFormType,
  EventoCancelamentoSchema,
  EventoEncerramentoFormType,
  EventoEncerramentoSchema,
  MDFeEventoInput,
  tipoEventoMDFeOptions,
} from "../MDFe.Evento.Dto";
import {
  MDFeEventoEnviar,
  MDFeEventoIncluir,
  MDFeFiltrar,
} from "src/services/Fiscal.Service";
import { MapearErrosServidor } from "src/hook/Functions";
import moment from "moment";
import { useSearchParams } from "react-router-dom";

interface Props {
  show: boolean;
  mdfeId: number | null;
  onClose: (value: boolean) => void;
  onReload: () => void;
  tipoEventoInicial?: number;
}

const MDFeEventoModalCadastro = ({
  show,
  mdfeId,
  onClose,
  onReload,
  tipoEventoInicial,
}: Props) => {
  const [searchParams] = useSearchParams();
  const [tipoEvento, setTipoEvento] = useState<number>(
    tipoEventoInicial ?? ETipoEventoMDFe.Cancelamento
  );
  const [submitting, setSubmitting] = useState(false);
  const consultaMunicipio = ConsultaMunicipioModule();

  const cancelamentoForm = useForm<EventoCancelamentoFormType>({
    resolver: yupResolver(EventoCancelamentoSchema),
    defaultValues: {
      justificativaCancelamento: "",
    },
  });

  const encerramentoForm = useForm<EventoEncerramentoFormType>({
    resolver: yupResolver(EventoEncerramentoSchema),
    defaultValues: {
      dataEncerramento: moment().format("YYYY-MM-DD"),
      codigoMunicipio: 0,
      nomeMunicipio: "",
    },
  });

  useEffect(() => {
    if (show) {
      if (tipoEventoInicial) {
        setTipoEvento(tipoEventoInicial);
      }
      encerramentoForm.setValue(
        "dataEncerramento",
        moment().format("YYYY-MM-DD")
      );

      const isEncerramento =
        (tipoEventoInicial ?? tipoEvento) === ETipoEventoMDFe.Encerramento;

      if (isEncerramento) {
        const codMunUrl = searchParams.get("codigoMunicipio");
        const nomeMunUrl = searchParams.get("nomeMunicipio");

        if (codMunUrl) {
          const cod = Number(codMunUrl);
          const nome = nomeMunUrl ? decodeURIComponent(nomeMunUrl) : "";
          encerramentoForm.reset({
            dataEncerramento: moment().format("YYYY-MM-DD"),
            codigoMunicipio: cod,
            nomeMunicipio: nome,
          });
        } else if (mdfeId && mdfeId > 0) {
          MDFeFiltrar({ id: mdfeId } as any)
            .then((resp) => {
              if (resp.data.success && resp.data.data?.length > 0) {
                const mdfe = resp.data.data[0];
                const ultDescarga =
                  mdfe.municipiosDescarga && mdfe.municipiosDescarga.length > 0
                    ? mdfe.municipiosDescarga[
                        mdfe.municipiosDescarga.length - 1
                      ]
                    : null;
                const cod =
                  ultDescarga?.codigoMunicipioDescarga ||
                  (ultDescarga as any)?.codigoMunicipio;
                const nome =
                  ultDescarga?.nomeMunicipioDescarga ||
                  (ultDescarga as any)?.nomeMunicipio;
                if (cod) {
                  encerramentoForm.reset({
                    dataEncerramento: moment().format("YYYY-MM-DD"),
                    codigoMunicipio: Number(cod),
                    nomeMunicipio: nome || "",
                  });
                }
              }
            })
            .catch((e) =>
              console.error(
                "Erro ao carregar dados do MDF-e para encerramento:",
                e
              )
            );
        }
      }
    }
  }, [show, tipoEventoInicial, mdfeId, searchParams]);

  const handleClose = () => {
    cancelamentoForm.reset();
    encerramentoForm.reset();
    onClose(false);
  };

  const handleSalvarETransmitir = async () => {
    if (!mdfeId || mdfeId <= 0) {
      showToast({
        message: "Identificador do MDF-e inválido",
        title: "Atenção",
        variant: "Warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      let payload: MDFeEventoInput;

      if (tipoEvento === ETipoEventoMDFe.Cancelamento) {
        const valid = await cancelamentoForm.trigger();
        if (!valid) {
          setSubmitting(false);
          return;
        }
        const data = cancelamentoForm.getValues();
        payload = {
          mdfeId,
          tipoEvento: ETipoEventoMDFe.Cancelamento,
          cancelamento: {
            justificativaCancelamento: data.justificativaCancelamento,
          },
        };
      } else {
        const valid = await encerramentoForm.trigger();
        if (!valid) {
          setSubmitting(false);
          return;
        }
        const data = encerramentoForm.getValues();
        payload = {
          mdfeId,
          tipoEvento: ETipoEventoMDFe.Encerramento,
          encerramento: {
            dataEncerramento: data.dataEncerramento,
            codigoMunicipio: Number(data.codigoMunicipio),
          },
        };
      }

      const responseIncluir = await MDFeEventoIncluir(payload);
      if (responseIncluir.data.success) {
        const eventoId = responseIncluir.data.data?.id || responseIncluir.data.data;
        showToast({
          message: "Evento registrado. Transmitindo à SEFAZ...",
          title: "Processando",
          variant: "Info",
        });

        if (eventoId && Number(eventoId) > 0) {
          const responseEnviar = await MDFeEventoEnviar(Number(eventoId));
          if (responseEnviar.data.success) {
            showToast({
              message: "Evento homologado com sucesso pela SEFAZ!",
              title: "Sucesso",
              variant: "Success",
            });
          } else {
            MapearErrosServidor(responseEnviar.data.notifications, showToast);
          }
        }
        handleClose();
        onReload();
      } else {
        MapearErrosServidor(responseIncluir.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao incluir/transmitir evento:", error);
      showToast({
        message: "Ocorreu erro ao processar o evento",
        title: "Erro",
        variant: "Danger",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Incluir Evento MDF-e</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3 mb-3">
          <Col xs={12}>
            <FloatSelect
              label="Tipo do Evento"
              options={tipoEventoMDFeOptions}
              register={{
                name: "tipoEventoSelect",
                onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
                  const novoTipo = Number(e.target.value);
                  setTipoEvento(novoTipo);
                  if (
                    novoTipo === ETipoEventoMDFe.Encerramento &&
                    !encerramentoForm.getValues("codigoMunicipio") &&
                    mdfeId &&
                    mdfeId > 0
                  ) {
                    MDFeFiltrar({ id: mdfeId } as any)
                      .then((resp) => {
                        if (resp.data.success && resp.data.data?.length > 0) {
                          const mdfe = resp.data.data[0];
                          const ultDescarga =
                            mdfe.municipiosDescarga &&
                            mdfe.municipiosDescarga.length > 0
                              ? mdfe.municipiosDescarga[
                                  mdfe.municipiosDescarga.length - 1
                                ]
                              : null;
                          const cod =
                            ultDescarga?.codigoMunicipioDescarga ||
                            (ultDescarga as any)?.codigoMunicipio;
                          const nome =
                            ultDescarga?.nomeMunicipioDescarga ||
                            (ultDescarga as any)?.nomeMunicipio;
                          if (cod) {
                            encerramentoForm.reset({
                              dataEncerramento: moment().format("YYYY-MM-DD"),
                              codigoMunicipio: Number(cod),
                              nomeMunicipio: nome || "",
                            });
                          }
                        }
                      })
                      .catch(() => {});
                  }
                },
                value: String(tipoEvento),
              } as any}
            />
          </Col>
        </Row>

        {tipoEvento === ETipoEventoMDFe.Cancelamento && (
          <Row className="g-3">
            <Col xs={12}>
              <FloatInput
                label="Justificativa do Cancelamento (15 a 255 caracteres)"
                registerField={cancelamentoForm.register(
                  "justificativaCancelamento"
                )}
                errorsMessage={
                  cancelamentoForm.formState.errors.justificativaCancelamento
                }
                inputMask="textarea"
                maxLength={255}
              />
              <small className="text-muted">
                O cancelamento só pode ser realizado em até 24 horas após a autorização e antes de iniciada a viagem.
              </small>
            </Col>
          </Row>
        )}

        {tipoEvento === ETipoEventoMDFe.Encerramento && (
          <Row className="g-3">
            <Col xs={12} md={4}>
              <FloatInput
                label="Data de Encerramento"
                registerField={encerramentoForm.register("dataEncerramento")}
                errorsMessage={
                  encerramentoForm.formState.errors.dataEncerramento
                }
                inputMask="data"
              />
            </Col>

            <Col xs={12} md={8}>
              <DropdownFilter<MunicipioDto>
                key={`mun-${encerramentoForm.watch("codigoMunicipio") || "0"}`}
                fetchData={consultaMunicipio.MunicipioBuscar}
                loading={consultaMunicipio.loading}
                labelDescription="Município de Encerramento (Descarga Final)"
                getDescription={(item: MunicipioDto) =>
                  `${item.nomeMunicipio} - ${item.ufMunicipio} (IBGE: ${item.codigoMunicipio})`
                }
                getId={(item: MunicipioDto) => item.codigoMunicipio}
                registeredFields={{
                  id: encerramentoForm.register("codigoMunicipio"),
                  description: encerramentoForm.register("nomeMunicipio"),
                }}
                errorsMessageDescription={
                  encerramentoForm.formState.errors.codigoMunicipio
                }
                onSelect={(item: MunicipioDto | undefined) => {
                  if (item) {
                    encerramentoForm.setValue(
                      "codigoMunicipio",
                      Number(item.codigoMunicipio)
                    );
                    encerramentoForm.setValue(
                      "nomeMunicipio",
                      item.nomeMunicipio
                    );
                  } else {
                    encerramentoForm.setValue("codigoMunicipio", 0);
                    encerramentoForm.setValue("nomeMunicipio", "");
                  }
                }}
              />
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSalvarETransmitir}
          disabled={submitting}
        >
          {submitting ? "Processando..." : "Salvar e Transmitir"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MDFeEventoModalCadastro;
